import { useState } from 'react'

import { useGenerateQuote } from 'components/hooks/useGenerateQuote'
import { Banner, BorderedRadioButton } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import QuoteFlowButtonNav from '../QuoteFlowButtonNav'
import QuoteFlowHeader from '../QuoteFlowHeader'
import { type QuoteGenerationOptions } from '../types'
import DynamicAllowanceIcon from './DynamicAllowanceIcon'
import FlatAllowanceIcon from './FlatAllowanceIcon'

type Props = {
  prospectId: string
  quoteId: string
  dynamicNextStepPath: string
  flatNextStepPath: string
  previousStepPath: string
  quoteGenerationOptions: QuoteGenerationOptions
}

export function SelectQuoteStrategy ({
  prospectId,
  quoteId,
  dynamicNextStepPath,
  flatNextStepPath,
  previousStepPath,
  quoteGenerationOptions,
}: Props) {
  const { assign } = useWindowLocation()

  const [budgetType, setBudgetType] = useState<'dynamic' | 'flat'>('dynamic')
  const [error, setError] = useState<string>()

  const generateQuoteMutation = useGenerateQuote({ prospectId, quoteId })

  const onClickNext = () => {
    if (budgetType === 'dynamic') {
      generateQuoteMutation.mutate({
        coverage_start_date: quoteGenerationOptions.coverage_start_date,
        year: quoteGenerationOptions.quote_year,
        metal_tiers: quoteGenerationOptions.metal_tiers,
        coverage_percentiles: quoteGenerationOptions.coverage_percentiles,
      }, {
        onSuccess: () => {
          assign(dynamicNextStepPath)
        },
        onError: (error) => {
          setError(error.message)
        },
      })
    } else {
      generateQuoteMutation.mutate({
        coverage_start_date: quoteGenerationOptions.coverage_start_date,
        year: quoteGenerationOptions.quote_year,
      }, {
        onSuccess: () => {
          assign(flatNextStepPath)
        },
        onError: (error) => {
          setError(error.message)
        },
      })
    }
  }

  return (
    <div className='flex justify-center'>
      <div className='flex flex-col max-w-2xl w-full'>
        <QuoteFlowHeader
          title='Budget type'
          description='Choose how you&apos;d like to approach budgeting'
        />
        <div className='grid grid-cols-2 grid-rows-[[badge]_auto_[radio]_1fr] gap-x-3 mb-10'>
          <div className='row-[badge]'>
            <div
              className=' inline-block text-xs font-semibold text-violet-500 bg-violet-50 rounded-t-lg py-1 px-2 ml-2'
            >
              Recommended
            </div>
          </div>
          <div className='row-[radio]'>
            <BorderedRadioButton
              value='dynamic'
              label='Dynamic budgeting'
              description='Thatch calculates a budget for each employee based on age, location, and your cost sharing preferences.'
              checked={budgetType === 'dynamic'}
              onChange={() => setBudgetType('dynamic')}
            >
              <div className='flex w-full flex-col items-center justify-center'>
                <DynamicAllowanceIcon />
              </div>
            </BorderedRadioButton>
          </div>
          <div className='row-[radio]'>
            <BorderedRadioButton
              value='flat'
              label='Flat budget for everyone'
              description='Specify an exact employee budget, nationwide or by state. This option should only be considered if you have less than 10 employees.'
              checked={budgetType === 'flat'}
              onChange={() => setBudgetType('flat')}
            >
              <div className='flex w-full flex-col items-center justify-center'>
                <FlatAllowanceIcon />
              </div>
            </BorderedRadioButton>
          </div>
        </div>
        <div className='flex flex-col gap-y-4'>
          {error && (
            <Banner
              title='Error generating quote'
              type='critical'
              onDismiss={() => setError(undefined)}
            >
              {error}
            </Banner>
          )}
          <QuoteFlowButtonNav
            onNext={onClickNext}
            onBack={() => {
              assign(previousStepPath)
            }}
            isLoading={generateQuoteMutation.isPending}
          />
        </div>
      </div>
    </div>
  )
}

export default (props: Props) => {
  return (
    <SelectQuoteStrategy {...props} />
  )
}
